// PersonaleView.jsx — amm-v3 (A4, ondata 31): la vista "Costo personale"
// (pagina 'personale') in React, flag-gated (shell_amm_v3_personale, default OFF).
//
// Replica 1:1 renderPersonale di admin-module.js r.2551-2712: stesso titolo
// (con ⓘ helpIcon delegato), sottotitolo, toggle 'Totali azienda', 'Esporta
// CSV', frecce anno, 4 KPI, tabelle PER DIPENDENTE (mese×componenti + riga
// Totale) o vista AGGREGATA per mese, badge 'mancante' (A5-033), tabella
// QUADRATURA versamenti coi badge di stato delegati, click su un mese → apre
// il cedolino (openCedolino v1).
//
// STATO CONDIVISO col v1 (parità di persistenza): anno su AMM.personaleAnno
// (r.750), toggle su AMM.personaleVistaAzienda (r.2665). COMMIT ATOMICO.
//
// DEVIAZIONI DICHIARATE (v3 non bug-compatibile qui):
//  D-A4.2 doppio click su 'Esporta CSV' = 1 richiesta (guardia ref; il v1 NON
//         guarda, r.2697).
//  D-A4.5 ricarica fallita con dati presenti ⇒ banner + Riprova, dati
//         CONSERVATI (famiglia D-A1.5/D-A3.2); a mount fresco errore pieno con
//         Riprova.

import React from 'react';
import { AM_NEW_TEXTS } from './api.js';
import {
  AP_TEXTS, MESI_S,
  personaleStato, mesiConDati, sommaTotaliAzienda, round2diff, mancantiLabel,
  aggTitolo, aggEmpty, emptyDip,
  readPersonale, apriCedolinoMese, esportaCsvPersonale,
} from './personale-api.js';

// HTML prodotto dalle primitive v1 (helpIcon / personaleStatoBadge): iniettato
// verbatim (pattern AnagraficaView.V1Html) — la delega document-level dell'ⓘ resta v1.
function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function StatoBox({ error, onRetry }) {
  if (error) {
    const msg = error && error.message ? error.message : String(error);
    return (
      <div role="alert" className="bg-white border rounded-lg p-6 text-center" style={{ borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>{'⚠ ' + msg}</div>
        <button type="button" style={{ marginTop: 12, background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={onRetry}>{AM_NEW_TEXTS.riprova}</button>
      </div>
    );
  }
  return <div className="text-slate-400 text-sm">Caricamento…</div>;
}

// KPI markup VERBATIM v1 (AMM.kpi r.1345-1347, small=true ⇒ text-xl)
function Kpi({ label, val, color }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-xl font-semibold mt-1" style={{ color }}>{val}</div>
    </div>
  );
}

export function PersonaleView({ win, tick }) {
  const A = win.AMM;
  const [pending, setPendingState] = React.useState(() => personaleStato(A));
  const setPending = (next) => {
    A.personaleAnno = next.anno;              // write-through (r.2695-2696)
    A.personaleVistaAzienda = next.vistaAzienda; // write-through (r.2694)
    setPendingState(next);
  };
  const [dati, setDati] = React.useState({ loading: true, error: null, vista: null });
  const [reloadN, setReloadN] = React.useState(0);
  const busyCsvRef = React.useRef(false);

  React.useEffect(() => {
    let vivo = true;
    const anno = pending.anno;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readPersonale(win, anno).then(
      (r) => { if (vivo) setDati({ loading: false, error: null, vista: { anno, ...r } }); },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN, pending.anno]);

  const vista = dati.vista;
  const anno = vista ? vista.anno : pending.anno;
  const vistaAzienda = pending.vistaAzienda;
  const f2 = (n) => A.fmtEur2(n);
  const t = vista ? vista.totali : {};

  const onExportCsv = async () => {
    if (busyCsvRef.current) return;
    busyCsvRef.current = true;
    try {
      try { await esportaCsvPersonale(win, anno); }
      catch (e) { A.toast(AP_TEXTS.exportFallito + (e && e.message ? e.message : e)); } // verbatim r.2699
    } finally {
      busyCsvRef.current = false;
    }
  };

  // celle numeriche di una riga mese (parità colori v1 r.2576-2582)
  const celleNum = (m) => [
    <td key="lorda" className="px-3 py-2 text-right text-sm tabular-nums">{f2(m.lorda)}</td>,
    <td key="irpef" className="px-3 py-2 text-right text-sm tabular-nums text-rose-700">{f2(m.irpef)}</td>,
    <td key="cdip" className="px-3 py-2 text-right text-sm tabular-nums text-slate-500">{f2(m.contributi_dip)}</td>,
    <td key="cditta" className="px-3 py-2 text-right text-sm tabular-nums text-slate-500">{f2(m.contributi_ditta)}</td>,
    <td key="tfr" className="px-3 py-2 text-right text-sm tabular-nums">{f2(m.tfr)}</td>,
    <td key="netto" className="px-3 py-2 text-right text-sm tabular-nums text-emerald-700">{f2(m.netto)}</td>,
    <td key="costo" className="px-3 py-2 text-right text-sm tabular-nums font-bold" style={{ color: '#0e7490' }}>{f2(m.costo_azienda)}</td>,
  ];

  const rigaMese = (dip, m, isTotale) => {
    const cls = isTotale ? 'bg-slate-100 font-semibold border-t-2 border-slate-300' : 'amm-pers-mese border-t hover:bg-cyan-50 cursor-pointer';
    const label = isTotale ? AP_TEXTS.rigaTotale : MESI_S[m.mese - 1];
    const extra = isTotale ? {} : { 'data-mese': m.mese, 'data-dip': dip.id, onClick: () => apriCedolinoMese(win, dip.id, m.mese, anno) };
    return (
      <tr key={isTotale ? 'tot' : m.mese} className={cls} {...extra}>
        <td className="px-3 py-2 text-sm">
          {label}
          {!isTotale && m.mancante && <>{' '}<span className="amm-badge" style={{ background: '#e2e8f0', color: '#64748b' }} title={AP_TEXTS.mancanteTitleSingola}>{AP_TEXTS.mancante}</span></>}
        </td>
        {celleNum(m)}
      </tr>
    );
  };

  const theadCosto = (
    <thead className="bg-slate-50 text-slate-500">
      <tr>
        <th className="px-3 py-2 text-xs uppercase font-medium text-left">{AP_TEXTS.colMese}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colLorda}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colIrpef}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colContrDip}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colContrDitta}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colTfr}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colNetto}</th>
        <th className="px-3 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.colCostoAz}</th>
      </tr>
    </thead>
  );

  const tabellaDip = (dip) => (
    <div key={dip.id} className="bg-white border rounded-lg overflow-hidden mb-4">
      <div className="px-4 py-2.5 border-b bg-slate-50 font-medium text-sm">{dip.nome}</div>
      <table className="w-full text-sm">
        {theadCosto}
        <tbody>
          {mesiConDati(dip).map((m) => rigaMese(dip, m, false))}
          {rigaMese(dip, dip.totali, true)}
        </tbody>
      </table>
    </div>
  );

  const rigaAgg = (a) => (
    <tr key={a.mese} className="border-t">
      <td className="px-3 py-2 text-sm">
        {MESI_S[a.mese - 1]}
        {a.mancanti ? <>{' '}<span className="amm-badge" style={{ background: '#e2e8f0', color: '#64748b' }} title={AP_TEXTS.mancanteTitleAgg}>{mancantiLabel(a.mancanti)}</span></> : null}
      </td>
      {celleNum(a)}
    </tr>
  );

  const tabellaTotaliAzienda = (dipendenti) => {
    const righe = sommaTotaliAzienda(dipendenti);
    if (!righe.length) return <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm mb-4">{aggEmpty(anno)}</div>;
    return (
      <div className="bg-white border rounded-lg overflow-hidden mb-4">
        <div className="px-4 py-2.5 border-b bg-slate-50 font-medium text-sm">{aggTitolo(dipendenti.length)}</div>
        <table className="w-full text-sm">
          {theadCosto}
          <tbody>{righe.map(rigaAgg)}</tbody>
        </table>
      </div>
    );
  };

  const quadCell = (cedolini, f24, stato) => {
    const delta = round2diff(cedolini, f24);
    return (
      <td className="px-2 py-2 text-right">
        <div className="text-xs text-slate-700 tabular-nums">{f2(cedolini)}</div>
        <div className="text-xs text-slate-400 tabular-nums">{f2(f24)}</div>
        <div className="mt-0.5">
          <V1Html html={A.personaleStatoBadge(stato)} />
          {stato === 'differenza' && <>{' '}<span className="text-[10px] text-amber-700">{f2(delta)}</span></>}
        </div>
      </td>
    );
  };

  return (
    <div data-amm-v3="personale">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">{AP_TEXTS.title}<V1Html html={A.helpIcon(AP_TEXTS.helpKey)} /></h1>
          <div className="text-xs text-slate-400">{AP_TEXTS.sub}</div>
        </div>
        <div className="flex items-center gap-2">
          <button id="pers-vista-toggle" type="button" className={(vistaAzienda ? 'bg-cyan-700 hover:bg-cyan-600 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-800') + ' text-xs px-3 py-2 rounded font-medium'} onClick={() => setPending({ ...pending, vistaAzienda: !pending.vistaAzienda })}>{vistaAzienda ? AP_TEXTS.toggleOn : AP_TEXTS.toggleOff}</button>
          <button id="amm-export-csv" type="button" className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs px-3 py-2 rounded font-medium" onClick={onExportCsv}>{AP_TEXTS.exportCsv}</button>
          <div className="flex items-center gap-1">
            <button id="pers-prev" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => setPending({ ...pending, anno: pending.anno - 1 })}>{'‹'}</button>
            <span className="px-2 font-semibold tabular-nums">{anno}</span>
            <button id="pers-next" type="button" className="px-2 py-1 border rounded text-sm bg-white" onClick={() => setPending({ ...pending, anno: pending.anno + 1 })}>{'›'}</button>
          </div>
        </div>
      </div>

      {/* D-A4.5: ricarica fallita con dati presenti ⇒ banner + Riprova */}
      {dati.error && vista && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" style={{ background: '#0e7490', color: '#fff', border: 0, borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }} onClick={() => setReloadN((n) => n + 1)}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      {!vista ? (
        <StatoBox error={dati.error} onRetry={() => setReloadN((n) => n + 1)} />
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3 mb-5">
            <Kpi label={AP_TEXTS.kpiCostoTot + anno} val={f2(t.costo_azienda)} color="#0e7490" />
            <Kpi label={AP_TEXTS.kpiNetto} val={f2(t.netto)} color="#059669" />
            <Kpi label={AP_TEXTS.kpiContrDitta} val={f2(t.contributi_ditta)} color="#0f172a" />
            <Kpi label={AP_TEXTS.kpiTfr} val={f2(t.tfr)} color="#0f172a" />
          </div>

          {vista.dipendenti && vista.dipendenti.length
            ? (vistaAzienda ? tabellaTotaliAzienda(vista.dipendenti) : vista.dipendenti.map(tabellaDip))
            : <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm mb-4">{emptyDip(anno)}</div>}

          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">{AP_TEXTS.quadTitolo}</div>
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-3 py-2 text-xs uppercase font-medium">{AP_TEXTS.quadColMese}</th>
                    <th className="px-2 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.quadColRitenute}</th>
                    <th className="px-2 py-2 text-xs uppercase font-medium text-right">{AP_TEXTS.quadColInps}</th>
                  </tr>
                </thead>
                <tbody>
                  {(vista.quadratura || []).map((q) => (
                    <tr key={q.mese} className="border-t">
                      <td className="px-3 py-2 text-sm font-medium">{MESI_S[q.mese - 1]}</td>
                      {quadCell(q.ritenute_cedolini, q.ritenute_f24, q.stato_ritenute)}
                      {quadCell(q.inps_cedolini, q.inps_f24, q.stato_inps)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-400 mt-2">{AP_TEXTS.quadNota}</div>
          </div>
        </>
      )}
    </div>
  );
}

// Factory (pattern createAmmV3Resoconto)
export function createAmmV3Personale(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0 && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1;
    root.render(<PersonaleView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
